import { Camera, SwitchCamera } from 'lucide-react';
import { IconCircleCheck } from '@tabler/icons-react';
import Webcam from 'react-webcam';
import { translations } from '../../../translations/chatInputTranslations';

type VideoTestProps = {
  isVideoReady: boolean;
  canSwitchCamera: boolean;
  handleSwitchCamera: () => void;
  isSwitchingCamera: boolean;
  webcamRef: React.RefObject<Webcam>;

  // Additional props needed for full functionality
  isFrontCamera: boolean;
  isStreamLoading: boolean;
  getVideoConstraints: () => any;
  onUserMediaError: (error: string | DOMException) => void;
  onUserMedia: () => void;
  languageCode?: string;
  isTextRightToLeft: boolean;
};

export function VideoTest({
  isVideoReady,
  canSwitchCamera,
  handleSwitchCamera,
  isSwitchingCamera,
  webcamRef,
  isFrontCamera,
  isStreamLoading,
  getVideoConstraints,
  onUserMediaError,
  onUserMedia,
  languageCode = 'en',
  isTextRightToLeft = false,
}: VideoTestProps) {
  const t = translations[languageCode] || translations.en;

  return (
    <div
      className={`space-y-2 border border-gray-200 ${isVideoReady ? 'border-green-300' : 'border-red-200'} rounded-lg p-4 ${isVideoReady ? 'bg-green-50' : 'bg-red-50'}`}
    >
      <div className="flex items-center justify-between relative">
        <h4 className="font-medium text-sm flex items-center gap-2">
          <Camera className="w-5 h-5" />
          {t.camera}
        </h4>
        <div
          className={`absolute ${isTextRightToLeft ? 'left-0' : 'right-0'} -top-0.5 flex items-center`}
        >
          {canSwitchCamera && (
            <button
              onClick={handleSwitchCamera}
              disabled={isSwitchingCamera}
              className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
              title={isSwitchingCamera ? t.switching : t.switchCamera}
            >
              <SwitchCamera
                className={`w-5 h-5 text-gray-600 ${isSwitchingCamera ? 'animate-spin opacity-75' : ''}`}
              />
            </button>
          )}
          {isVideoReady && (
            <div className="ml-auto">
              <IconCircleCheck className="w-6 h-6 text-white fill-green-600" />
            </div>
          )}
        </div>
      </div>
      <div className="relative">
        <Webcam
          ref={webcamRef}
          audio={false}
          muted
          playsInline
          videoConstraints={getVideoConstraints()}
          className="w-full h-48 bg-gray-900 rounded-lg object-cover"
          style={{
            transform: isFrontCamera ? 'scaleX(-1)' : 'none',
          }}
          onUserMediaError={onUserMediaError}
          onUserMedia={onUserMedia}
        />

        {/* Loading spinner overlay */}
        {isStreamLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900 rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        )}
      </div>
    </div>
  );
}
